import ExcelJS from 'exceljs'

import { formatearMedioPago, obtenerEstadoPago } from './gastos'
import {
  formatearMontoPos,
  obtenerEstadoEnvasesPos,
  obtenerEstadoVentaPos,
  parsearFechaLocalPos,
} from './pos'

export function descargarBuffer(buffer, nombreArchivo) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const enlace = document.createElement('a')

  enlace.href = url
  enlace.download = nombreArchivo
  document.body.appendChild(enlace)
  enlace.click()
  document.body.removeChild(enlace)
  URL.revokeObjectURL(url)
}

// Genera un .xlsx real (ExcelJS, no HTML disfrazado de Excel) y dispara
// la descarga. `columnas`: [{ encabezado, clave, ancho, formato }].
// `filas`: objetos planos con esas claves — los valores numéricos deben
// llegar como number (no como string con "$") para que Excel pueda sumar
// y ordenar la columna correctamente.
export async function generarYDescargarExcel({
  nombreArchivo,
  nombreHoja,
  columnas,
  filas,
}) {
  const libro = new ExcelJS.Workbook()
  libro.creator = 'Franco Frutt Admin'
  libro.created = new Date()

  const hoja = libro.addWorksheet(nombreHoja || 'Datos')

  hoja.columns = columnas.map((columna) => ({
    header: columna.encabezado,
    key: columna.clave,
    width: columna.ancho || 18,
  }))

  hoja.getRow(1).font = { bold: true }
  hoja.getRow(1).alignment = { vertical: 'middle' }

  filas.forEach((fila) => hoja.addRow(fila))

  columnas.forEach((columna, indice) => {
    if (columna.formato) {
      hoja.getColumn(indice + 1).numFmt = columna.formato
    }
  })

  if (columnas.length > 0) {
    hoja.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columnas.length },
    }
  }

  const buffer = await libro.xlsx.writeBuffer()

  descargarBuffer(buffer, nombreArchivo)
}

// "0.##" (no "0.00") para no forzar ",00" en montos enteros — igual
// criterio que formatearMoneda: hasta 2 decimales, solo si existen.
const FORMATO_MONTO = '"$"#,##0.##'
const FORMATO_FECHA = 'dd-mm-yyyy'

// Junta los pagos vigentes (estado = 'registrado', ya filtrado por quien
// arma el mapa de pagos) de una venta en un texto tipo
// "Transferencia $10.000 | Efectivo $20.000". Sin pagos: "Pendiente".
export function construirTextoMediosPago(pagos) {
  if (!pagos || pagos.length === 0) return 'Pendiente'

  const totalesPorMedio = new Map()

  for (const pago of pagos) {
    const actual = totalesPorMedio.get(pago.medio_pago) || 0
    totalesPorMedio.set(
      pago.medio_pago,
      actual + (Number(pago.monto) || 0)
    )
  }

  return Array.from(totalesPorMedio.entries())
    .map(
      ([medio, monto]) =>
        `${formatearMedioPago(medio)} ${formatearMontoPos(monto)}`
    )
    .join(' | ')
}

// "Arándano / Rejilla verde | Arándano / Caja 3/4" — una línea por
// producto+envase de la venta (nunca una fila de Excel por línea: eso lo
// resuelve quien arma las filas, agrupando todo en esta única columna).
export function construirTextoProductos(detalles, productosPorId) {
  if (!detalles || detalles.length === 0) return '—'

  return detalles
    .map((detalle) => {
      const nombreProducto =
        productosPorId.get(detalle.producto_id)?.nombre || 'Producto'

      return `${nombreProducto} / ${detalle.envase_nombre}`
    })
    .join(' | ')
}

const COLUMNAS_EXCEL_VENTAS = [
  { encabezado: 'Fecha', clave: 'fecha', ancho: 14, formato: FORMATO_FECHA },
  { encabezado: 'Cliente', clave: 'cliente', ancho: 28 },
  { encabezado: 'Estado venta', clave: 'estadoVenta', ancho: 14 },
  { encabezado: 'Estado pago', clave: 'estadoPago', ancho: 14 },
  { encabezado: 'Cant. productos', clave: 'cantidadProductos', ancho: 14 },
  { encabezado: 'Kilos netos', clave: 'kilosNetos', ancho: 14 },
  { encabezado: 'Subtotal', clave: 'subtotal', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Descuento', clave: 'descuento', ancho: 14, formato: FORMATO_MONTO },
  { encabezado: 'Total venta', clave: 'totalVenta', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Monto pagado', clave: 'montoPagado', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Saldo pendiente', clave: 'saldoPendiente', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Estado Cajas/Rejillas', clave: 'estadoEnvases', ancho: 18 },
  { encabezado: 'Monto pagado Cajas/Rejillas', clave: 'montoEnvasesPagado', ancho: 20, formato: FORMATO_MONTO },
  { encabezado: 'Usuario', clave: 'usuario', ancho: 22 },
  { encabezado: 'Medios de pago', clave: 'mediosPago', ancho: 32 },
  { encabezado: 'Productos', clave: 'productos', ancho: 40 },
]

// `contexto`: { detallePorVenta, pagosPorVenta, productosPorId, usuariosPorId }
// — los mismos mapas ya cargados en bloque que usa la pantalla para
// filtrar; exportar no dispara ninguna consulta adicional.
export async function exportarVentasPosExcel(ventas, contexto, nombreArchivo) {
  const { detallePorVenta, pagosPorVenta, productosPorId, usuariosPorId } =
    contexto

  const filas = ventas.map((venta) => {
    const detalles = detallePorVenta.get(venta.id) || []
    const pagos = pagosPorVenta.get(venta.id) || []

    return {
      fecha: parsearFechaLocalPos(venta.fecha_venta),
      cliente: venta.cliente_razon_social || 'Sin cliente',
      estadoVenta: obtenerEstadoVentaPos(venta.estado).etiqueta,
      estadoPago: obtenerEstadoPago(venta.estado_pago).etiqueta,
      cantidadProductos: Number(venta.cantidad_items) || 0,
      kilosNetos: Number(venta.total_kilos_netos) || 0,
      subtotal: Number(venta.subtotal_venta) || 0,
      descuento: Number(venta.monto_descuento) || 0,
      totalVenta: Number(venta.total_venta) || 0,
      montoPagado: Number(venta.monto_pagado) || 0,
      saldoPendiente: Number(venta.saldo_pendiente) || 0,
      estadoEnvases: obtenerEstadoEnvasesPos(venta.estado_envases)
        .etiqueta,
      montoEnvasesPagado: Number(venta.monto_envases_pagado) || 0,
      usuario: usuariosPorId.get(venta.creado_por) || '—',
      mediosPago: construirTextoMediosPago(pagos),
      productos: construirTextoProductos(detalles, productosPorId),
    }
  })

  await generarYDescargarExcel({
    nombreArchivo,
    nombreHoja: 'Ventas POS',
    columnas: COLUMNAS_EXCEL_VENTAS,
    filas,
  })
}

const COLUMNAS_EXCEL_PAGOS_PENDIENTES = [
  { encabezado: 'Fecha venta', clave: 'fecha', ancho: 14, formato: FORMATO_FECHA },
  { encabezado: 'Cliente', clave: 'cliente', ancho: 28 },
  { encabezado: 'Total venta', clave: 'totalVenta', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Monto pagado', clave: 'montoPagado', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Saldo pendiente', clave: 'saldoPendiente', ancho: 16, formato: FORMATO_MONTO },
  { encabezado: 'Estado pago', clave: 'estadoPago', ancho: 14 },
  { encabezado: 'Medios pagados hasta ahora', clave: 'mediosPago', ancho: 32 },
  { encabezado: 'Estado Cajas/Rejillas', clave: 'estadoEnvases', ancho: 18 },
  { encabezado: 'Usuario', clave: 'usuario', ancho: 22 },
  { encabezado: 'Productos', clave: 'productos', ancho: 40 },
]

export async function exportarPagosPendientesPosExcel(
  ventas,
  contexto,
  nombreArchivo
) {
  const { detallePorVenta, pagosPorVenta, productosPorId, usuariosPorId } =
    contexto

  const filas = ventas.map((venta) => {
    const detalles = detallePorVenta.get(venta.id) || []
    const pagos = pagosPorVenta.get(venta.id) || []

    return {
      fecha: parsearFechaLocalPos(venta.fecha_venta),
      cliente: venta.cliente_razon_social || 'Sin cliente',
      totalVenta: Number(venta.total_venta) || 0,
      montoPagado: Number(venta.monto_pagado) || 0,
      saldoPendiente: Number(venta.saldo_pendiente) || 0,
      estadoPago: obtenerEstadoPago(venta.estado_pago).etiqueta,
      mediosPago: construirTextoMediosPago(pagos),
      estadoEnvases: obtenerEstadoEnvasesPos(venta.estado_envases)
        .etiqueta,
      usuario: usuariosPorId.get(venta.creado_por) || '—',
      productos: construirTextoProductos(detalles, productosPorId),
    }
  })

  await generarYDescargarExcel({
    nombreArchivo,
    nombreHoja: 'Pagos pendientes',
    columnas: COLUMNAS_EXCEL_PAGOS_PENDIENTES,
    filas,
  })
}
